import type { CurrentCommunity } from "@circle-react/types/CurrentCommunity";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

import { communityApi } from "../api/communityApi";
import type { LoadingStatus } from "./loadingStatuses";
import { LOADED, LOADING, REFRESHING } from "./loadingStatuses";

const initialState = {
  status: LOADING,
  record: {
    home_page_cover_image: undefined,
  },
  actions: {
    refresh: () => Promise.resolve(),
    loadData: () => Promise.resolve(),
  },
  helpers: {
    isDataRefreshing: () => false,
    isDataLoading: () => false,
    isDataLoaded: () => false,
    communityVisibilityText: () => "Public",
  },
};

export interface Actions {
  loadData: () => Promise<void>;
  refresh: () => Promise<void>;
}

export interface Helpers {
  communityVisibilityText: () => string;
  isDataLoaded: () => boolean;
  isDataLoading: () => boolean;
  isDataRefreshing: () => boolean;
}

export interface CurrentCommunityContextValue {
  actions: Actions;
  helpers: Helpers;
  record: Partial<CurrentCommunity>;
  status: LoadingStatus;
}

const CurrentCommunityContext =
  createContext<CurrentCommunityContextValue>(initialState);
CurrentCommunityContext.displayName = "CurrentCommunityContext";

interface CurrentCommunityContextReProviderProps {
  children: ReactNode;
  value: CurrentCommunityContextValue;
}

export const CurrentCommunityContextReProvider = ({
  value,
  children,
}: CurrentCommunityContextReProviderProps) => (
  <CurrentCommunityContext.Provider value={value}>
    {children}
  </CurrentCommunityContext.Provider>
);

export interface CurrentCommunityContextProps {
  children: ReactNode;
  community?: CurrentCommunity;
  communityId?: number;
}

export const CurrentCommunityContextProvider = ({
  children,
  community,
}: CurrentCommunityContextProps) => {
  const [status, setStatus] = useState<LoadingStatus>(LOADING);
  const [record, setRecord] = useState<Partial<CurrentCommunity>>(
    initialState.record,
  );
  const { is_private: isPrivate } = record;

  const onDataLoadSuccess = useCallback((data: any) => {
    setRecord(data);
    setStatus(LOADED);
  }, []);

  const loadData = useCallback(async () => {
    const response = await communityApi.show<Response>();

    if (response?.ok) {
      const data = await response.json();
      onDataLoadSuccess(data);
    } else {
      // Add error handling. May be show toast message
    }
  }, [onDataLoadSuccess]);

  useEffect(() => {
    if (community) {
      setRecord(community);
      setStatus(LOADED);
    } else {
      void loadData();
    }
    // Intentionally mount-once - `community`/`loadData` are omitted so a
    // parent re-render doesn't re-trigger this.
  }, []);

  const refresh = useCallback(async () => {
    setStatus(REFRESHING);
    await loadData();
  }, [loadData]);

  const communityVisibilityText = useCallback(
    () => (isPrivate ? "Private" : "Public"),
    [isPrivate],
  );

  const isDataLoaded = useCallback(() => status === LOADED, [status]);
  const isDataLoading = useCallback(() => status === LOADING, [status]);
  const isDataRefreshing = useCallback(() => status === REFRESHING, [status]);

  // Memoized so consumers that use this value as a dependency (e.g.
  // useRegisterTopBarOverride) don't see a new reference - and re-run -
  // on every render that doesn't actually change status/record. The
  // actions/helpers are all useCallback-stabilized above so they can be
  // listed here without defeating the memo.
  const value = useMemo(
    () => ({
      status,
      record,
      actions: { loadData, refresh },
      helpers: {
        isDataLoading,
        isDataRefreshing,
        isDataLoaded,
        communityVisibilityText,
      },
    }),
    [
      status,
      record,
      loadData,
      refresh,
      isDataLoading,
      isDataRefreshing,
      isDataLoaded,
      communityVisibilityText,
    ],
  );
  return (
    <CurrentCommunityContext.Provider value={value}>
      {children}
    </CurrentCommunityContext.Provider>
  );
};

export const useCurrentCommunityContext = () =>
  useContext(CurrentCommunityContext);
